import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import api from '../api/axios';
import { toast } from 'sonner';
import {
  CalendarClock, Scale, Receipt, Settings2, Loader2, X,
} from 'lucide-react';

// ── Tokens Pulse ──────────────────────────────────────────────────────────────
const BRAND = '#635BFF';
const BRAND_DARK = '#4B44D6';
const BRAND_SOFT = '#EEEDFF';
const BRAND_BORDER = '#D6D3FF';

const fmt = (n, dec = 0) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN',
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(n || 0);

const fechaCorta = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${parseInt(d)} ${meses[parseInt(m) - 1]} ${y}`;
};

const PillDias = ({ dias, proyectado }) => {
  if (proyectado) return <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500">proyectado</span>;
  if (dias < 0) return <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-red-100 text-red-600">vencida</span>;
  if (dias <= 15) return <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-red-50 text-red-500">en {dias} días</span>;
  if (dias <= 30) return <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-600">en {dias} días</span>;
  return <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500">en {dias} días</span>;
};

// ── KPI card ──────────────────────────────────────────────────────────────────
const Kpi = ({ label, value, sub, tag, tagColor }) => (
  <div className="relative bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
    {tag && (
      <span className={`absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full ${tagColor}`}>
        {tag}
      </span>
    )}
    <p className="text-xs font-semibold text-slate-500">{label}</p>
    <p className="font-mono text-2xl font-bold mt-1.5 text-slate-900 tracking-tight">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
  </div>
);

// ── Modal de configuración (CU, pérdidas, PTU) ────────────────────────────────
const ConfigModal = ({ config, onClose, onSaved }) => {
  const [cu, setCu] = useState(config?.coeficiente_utilidad ?? '');
  const [perdidas, setPerdidas] = useState(config?.perdidas_por_amortizar ?? 0);
  const [ptu, setPtu] = useState(config?.ptu_pagada ?? 0);
  const [pctGravado, setPctGravado] = useState(config?.pct_actos_gravados ?? 100);
  const [regimen, setRegimen] = useState(config?.regimen_fiscal ?? '');
  const [saving, setSaving] = useState(false);
  const catalogoRegimenes = config?.catalogo_regimenes || [];
  // Regímenes donde el CU no aplica (el ISR se calcula con tarifa/tasa propia)
  const esRegimenPF = ['606', '608', '612', '621', '625', '626'].includes(regimen);

  const guardar = async () => {
    const cuNum = cu === '' || cu === null ? 0 : parseFloat(cu);
    if (isNaN(cuNum) || cuNum < 0 || cuNum > 1) {
      toast.error('El coeficiente de utilidad debe estar entre 0 y 1 (ej. 0.0842)');
      return;
    }
    if (!esRegimenPF && !cuNum && regimen) {
      toast.error('Para personas morales captura el coeficiente de utilidad');
      return;
    }
    setSaving(true);
    try {
      await api.post('/fiscal/config', {
        coeficiente_utilidad: cuNum,
        perdidas_por_amortizar: parseFloat(perdidas) || 0,
        ptu_pagada: parseFloat(ptu) || 0,
        pct_actos_gravados: Math.min(Math.max(parseFloat(pctGravado) || 100, 0), 100),
        regimen_fiscal: regimen || null,
      });
      toast.success('Configuración fiscal guardada');
      onSaved();
    } catch {
      toast.error('No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800 text-base">Parámetros fiscales</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="fiscal-config-close">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              Régimen fiscal de la empresa (catálogo SAT)
            </label>
            <select
              value={regimen}
              onChange={e => setRegimen(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
              data-testid="select-regimen-fiscal"
            >
              <option value="">— Sin especificar (se calcula como PM general) —</option>
              {catalogoRegimenes.map(r => (
                <option key={r.codigo} value={r.codigo}>{r.codigo} · {r.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">
              Define cómo se calcula tu ISR: PM (CU × 30%), RESICO PF (tasa 1.0–2.5% menos
              retención 1.25%), PF actividad empresarial/profesional (tarifa Art. 96 acumulada
              con retenciones del 10% acreditables).
            </p>
          </div>
          <div style={esRegimenPF ? { opacity: 0.5 } : {}}>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              Coeficiente de utilidad (declaración anual anterior)
              {esRegimenPF && <span className="text-slate-400"> — no aplica a tu régimen</span>}
            </label>
            <input
              type="number" step="0.0001" min="0" max="1" value={cu}
              onChange={e => setCu(e.target.value)} placeholder="0.0842"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': BRAND }}
              data-testid="input-cu"
            />
            <p className="text-[11px] text-slate-400 mt-1">Lo encuentras en tu última declaración anual (Art. 14 LISR).</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Pérdidas fiscales por amortizar (MXN)</label>
            <input
              type="number" min="0" value={perdidas}
              onChange={e => setPerdidas(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2"
              data-testid="input-perdidas"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">PTU pagada en el ejercicio (MXN)</label>
            <input
              type="number" min="0" value={ptu}
              onChange={e => setPtu(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2"
              data-testid="input-ptu"
            />
            <p className="text-[11px] text-slate-400 mt-1">Se disminuye por partes iguales en provisionales de mayo a diciembre.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              % de cobros gravados al 16% (clientes sin tasa configurada)
            </label>
            <input
              type="number" min="0" max="100" step="1" value={pctGravado}
              onChange={e => setPctGravado(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2"
              data-testid="input-pct-gravado"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Tip: configura la tasa de IVA por cliente en <b>Catálogo → Clientes</b> (16%, 8% frontera, 0%/exento).
              Los clientes con tasa propia ignoran este porcentaje.
            </p>
          </div>
          <button
            onClick={guardar} disabled={saving}
            className="w-full py-2.5 rounded-lg text-white text-sm font-bold transition-colors disabled:opacity-60"
            style={{ background: BRAND }}
            data-testid="btn-guardar-config"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Página principal ──────────────────────────────────────────────────────────
export default function PlaneacionFiscal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('resumen');
  const [showConfig, setShowConfig] = useState(false);
  const [estrategias, setEstrategias] = useState(null);
  const [loadingEstr, setLoadingEstr] = useState(false);
  const [retenciones, setRetenciones] = useState(null);
  const [loadingRet, setLoadingRet] = useState(false);
  const ejercicio = new Date().getFullYear();

  const generarEstrategias = async () => {
    if (!data) return;
    setLoadingEstr(true);
    try {
      const r = await api.post('/fiscal/estrategias', { resumen: data });
      setEstrategias(r.data);
    } catch {
      toast.error('No se pudieron generar las estrategias');
    } finally {
      setLoadingEstr(false);
    }
  };

  const cargar = useCallback(() => {
    setLoading(true);
    api.get(`/fiscal/resumen?ejercicio=${ejercicio}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('No se pudo cargar la información fiscal'))
      .finally(() => setLoading(false));
  }, [ejercicio]);

  useEffect(() => { cargar(); }, [cargar]);

  // Facturas con retenciones — se cargan al abrir el tab
  useEffect(() => {
    if (tab !== 'retenciones' || retenciones) return;
    setLoadingRet(true);
    api.get(`/fiscal/retenciones?ejercicio=${ejercicio}`)
      .then(r => setRetenciones(r.data))
      .catch(() => toast.error('No se pudieron cargar las retenciones'))
      .finally(() => setLoadingRet(false));
  }, [tab, retenciones, ejercicio]);

  const k = data?.kpis || {};
  const cfg = data?.config || {};
  const meses = data?.meses || [];
  const maxIng = Math.max(...meses.map(m => m.ingresos_nominales), 1);
  const saldoAnual = (k.isr_anual_estimado || 0) - (k.isr_provisionales_pagados || 0) - (k.isr_provisionales_proyectados || 0);

  const TABS = [
    { id: 'resumen', label: 'Resumen' },
    { id: 'calendario', label: 'Calendario SAT' },
    { id: 'iva', label: 'IVA & Deducciones' },
    { id: 'retenciones', label: 'Retenciones' },
    { id: 'estrategias', label: '✨ Estrategias IA' },
  ];

  // Próxima obligación de pago (para tags "Vence" en KPIs y semáforo)
  const proxPago = (data?.calendario || []).find(o => (o.obligacion || '').includes('provisional') && !o.proyectado);
  const venceTag = proxPago ? `Vence ${fechaCorta(proxPago.vence).slice(0, 6)}` : 'por pagar';
  const PRIO_STYLE = {
    alta: 'bg-red-50 text-red-500',
    media: 'bg-amber-50 text-amber-600',
    baja: 'bg-slate-100 text-slate-500',
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="📋 Planeación Fiscal"
        subtitle={`Proyecta tus impuestos, anticipa obligaciones SAT y detecta ahorros — Ejercicio ${ejercicio}`}
        actions={
          <button
            onClick={() => setShowConfig(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-[13px] font-semibold"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)' }}
            data-testid="btn-config-fiscal"
          >
            <Settings2 size={15} /> Parámetros
          </button>
        }
      />

      {loading && (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Calculando proyección fiscal…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Nota de fuentes — como el mockup */}
          <div className="rounded-xl px-4 py-3 text-xs leading-relaxed"
               style={{ background: BRAND_SOFT, border: `1px solid ${BRAND_BORDER}`, color: '#3F3A8F' }}>
            💡 <b>Cómo se alimenta:</b> el ISR usa tus CFDIs emitidos y se calcula según tu <b>régimen
            fiscal</b> (PM: CU × 30% · RESICO PF: tasa 1.0–2.5% · PF actividad: tarifa Art. 96); el IVA usa
            el flujo real cobrado/pagado del motor de Cash Flow (LIVA 1-B). Las <b>retenciones de ISR/IVA</b>
            que te aplican tus clientes se leen automáticamente de tus CFDIs y se acreditan en el cálculo
            (ver tab Retenciones).
          </div>

          {!cfg.configurado && (
            <div className="rounded-xl px-4 py-3 text-sm flex items-center justify-between"
                 style={{ background: BRAND_SOFT, border: `1px solid ${BRAND_BORDER}`, color: BRAND_DARK }}>
              <span>
                ⚙️ <b>Configura tu coeficiente de utilidad</b> para que el ISR provisional se calcule con tu dato real
                (por ahora se muestra en $0).
              </span>
              <button
                onClick={() => setShowConfig(true)}
                className="text-xs font-bold px-3 py-1.5 rounded-lg text-white shrink-0 ml-3"
                style={{ background: BRAND }}
                data-testid="btn-config-banner"
              >
                Configurar
              </button>
            </div>
          )}

          {/* Sub-tabs */}
          <div className="flex gap-1.5 bg-white border border-slate-200 rounded-xl p-1 w-fit shadow-sm">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-1.5 rounded-lg text-[13px] font-semibold transition-colors ${
                  tab === t.id ? 'text-white' : 'text-slate-500 hover:text-slate-700'
                }`}
                style={tab === t.id ? { background: BRAND } : {}}
                data-testid={`fiscal-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* KPIs — siempre visibles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi
              label={`ISR provisional · ${k.periodo || '—'}`}
              value={fmt(k.isr_provisional_periodo)}
              sub={cfg.modo_calculo === 'resico_pf'
                ? 'RESICO PF: tasa 1.0–2.5% − retención 1.25%'
                : cfg.modo_calculo === 'pf_actividad'
                  ? 'Tarifa Art. 96 acumulada − retenciones 10%'
                  : cfg.configurado ? `CU ${cfg.coeficiente_utilidad} × 30%` : 'Configura tu CU'}
              tag={venceTag} tagColor="bg-red-50 text-red-500"
            />
            <Kpi
              label={k.iva_por_pagar_periodo > 0 ? `IVA por pagar · ${k.periodo || '—'}` : `IVA a favor · ${k.periodo || '—'}`}
              value={k.iva_por_pagar_periodo > 0 ? fmt(k.iva_por_pagar_periodo) : fmt(k.iva_saldo_favor_periodo)}
              sub={data.iva_base === 'flujo'
                ? `Trasladado ${fmt(k.iva_trasladado_periodo)} − Acreditable ${fmt(k.iva_acreditable_periodo)}${k.iva_saldo_favor_periodo > 0 && k.iva_por_pagar_periodo > 0 ? '' : ' · con arrastre'}`
                : `Trasladado ${fmt(k.iva_trasladado_periodo)} − Acreditable ${fmt(k.iva_acreditable_periodo)}`}
              tag={k.iva_por_pagar_periodo > 0 ? venceTag : 'a favor'}
              tagColor={k.iva_por_pagar_periodo > 0 ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}
            />
            {cfg.modo_calculo === 'resico_pf' || cfg.modo_calculo === 'pf_actividad' ? (
              <Kpi
                label="Retenciones a tu favor (acum.)"
                value={fmt((k.ret_isr_sufrida_acum || 0) + (k.ret_iva_sufrida_acum || 0))}
                sub={`ISR ${fmt(k.ret_isr_sufrida_acum)} · IVA ${fmt(k.ret_iva_sufrida_acum)} — de tus CFDIs`}
                tag={cfg.regimen_fiscal || 'PF'} tagColor="bg-indigo-50 text-indigo-600"
              />
            ) : (
              <Kpi
                label="Coeficiente de utilidad"
                value={cfg.configurado ? cfg.coeficiente_utilidad : '—'}
                sub="Editable en Parámetros"
                tag="manual" tagColor="bg-indigo-50 text-indigo-600"
              />
            )}
            <Kpi
              label="ISR anual estimado"
              value={fmt(k.isr_anual_estimado)}
              sub={`Pagado ${fmt(k.isr_provisionales_pagados)} · Por pagar ${fmt(k.isr_provisionales_proyectados)}`}
              tag={`cierre ${ejercicio}`} tagColor="bg-emerald-50 text-emerald-600"
            />
          </div>

          {/* ══ TAB: RESUMEN ══ */}
          {tab === 'resumen' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Proyección mensual */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-[13px] font-bold text-slate-700 flex items-center gap-2">
                    <Scale size={14} style={{ color: BRAND }} /> Ingresos nominales {ejercicio}
                  </h3>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: BRAND_SOFT, color: BRAND_DARK, border: `1px solid ${BRAND_BORDER}` }}>
                    fuente: CFDIs emitidos
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-end gap-1.5 h-32">
                    {meses.map(m => (
                      <div key={m.mes} className="flex-1 flex flex-col items-center gap-1" title={`${m.label}: ${fmt(m.ingresos_nominales)}`}>
                        <div
                          className="w-full rounded-t-md"
                          style={{
                            height: `${Math.max((m.ingresos_nominales / maxIng) * 110, 3)}px`,
                            background: m.es_real ? BRAND : BRAND_SOFT,
                            border: m.es_real ? 'none' : `1px dashed ${BRAND_BORDER}`,
                          }}
                        />
                        <span className="text-[9px] font-semibold text-slate-400">{m.label.toUpperCase()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-4 mt-3 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: BRAND }} /> Real (facturado)</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: BRAND_SOFT, border: `1px dashed ${BRAND_BORDER}` }} /> Proyectado (prom. últ. 3 meses)</span>
                  </div>
                  <table className="w-full text-xs mt-4">
                    <tbody>
                      <tr className="border-b border-slate-50">
                        <td className="py-1.5 text-slate-600">ISR anual estimado (cierre {ejercicio})</td>
                        <td className="py-1.5 text-right font-mono font-bold">{fmt(k.isr_anual_estimado)}</td>
                      </tr>
                      <tr className="border-b border-slate-50">
                        <td className="py-1.5 text-slate-600">Provisionales efectuados (meses reales)</td>
                        <td className="py-1.5 text-right font-mono">{fmt(k.isr_provisionales_pagados)}</td>
                      </tr>
                      <tr className="border-b border-slate-50">
                        <td className="py-1.5 text-slate-600">Provisionales proyectados (resto del año)</td>
                        <td className="py-1.5 text-right font-mono">{fmt(k.isr_provisionales_proyectados)}</td>
                      </tr>
                      <tr>
                        <td className="py-1.5 font-semibold text-slate-700">Saldo estimado en anual</td>
                        <td className={`py-1.5 text-right font-mono font-bold ${saldoAnual > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                          {saldoAnual > 0 ? `${fmt(saldoAnual)} a cargo` : 'sin saldo a cargo'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Detalle mensual ISR/IVA */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="px-4 py-3 border-b border-slate-100">
                  <h3 className="text-[13px] font-bold text-slate-700 flex items-center gap-2">
                    <Receipt size={14} style={{ color: BRAND }} /> Provisionales por mes
                  </h3>
                </div>
                <div className="p-2 overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-slate-100 text-slate-400">
                        <th className="text-left py-2 px-2 font-semibold">Mes</th>
                        <th className="text-right py-2 px-2 font-semibold">Facturado</th>
                        {data.iva_base === 'flujo' && <th className="text-right py-2 px-2 font-semibold">Cobrado</th>}
                        {data.iva_base === 'flujo' && <th className="text-right py-2 px-2 font-semibold">Pagado</th>}
                        <th className="text-right py-2 px-2 font-semibold">ISR prov.</th>
                        <th className="text-right py-2 px-2 font-semibold">IVA</th>
                        <th className="text-center py-2 px-2 font-semibold">CFDIs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {meses.map(m => (
                        <tr key={m.mes} className={`border-b border-slate-50 ${!m.es_real ? 'opacity-60' : ''}`} data-testid={`fila-mes-${m.mes}`}>
                          <td className="py-1.5 px-2 font-medium text-slate-700">
                            {m.label} {!m.es_real && <span className="text-[9px] text-slate-400">(proy.)</span>}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono text-slate-600">{fmt(m.ingresos_nominales)}</td>
                          {data.iva_base === 'flujo' && (
                            <td className="py-1.5 px-2 text-right font-mono text-slate-600">
                              {m.cobrado != null ? fmt(m.cobrado) : '—'}
                            </td>
                          )}
                          {data.iva_base === 'flujo' && (
                            <td className="py-1.5 px-2 text-right font-mono text-slate-600">
                              {m.pagado != null ? fmt(m.pagado) : '—'}
                            </td>
                          )}
                          <td className="py-1.5 px-2 text-right font-mono text-slate-800">{fmt(m.isr_provisional)}</td>
                          <td className={`py-1.5 px-2 text-right font-mono ${m.iva_por_pagar > 0 ? 'text-slate-800' : 'text-emerald-600'}`}>
                            {m.iva_por_pagar > 0
                              ? fmt(m.iva_por_pagar)
                              : (m.iva_saldo_favor > 0 ? `${fmt(m.iva_saldo_favor)} a favor` : fmt(0))}
                          </td>
                          <td className="py-1.5 px-2 text-center text-slate-400 font-mono">{m.cfdis_emitidos + m.cfdis_recibidos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Semáforo — visible en el tab Resumen */}
          {tab === 'resumen' && (
            <div>
              <p className="text-sm font-extrabold text-slate-800 mb-2">🚦 Semáforo fiscal</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  {
                    ok: !proxPago || proxPago.dias_restantes > 15,
                    warn: proxPago && proxPago.dias_restantes <= 15 && proxPago.dias_restantes > 5,
                    titulo: 'Próximo pago SAT',
                    sub: proxPago ? `${fechaCorta(proxPago.vence)} · en ${proxPago.dias_restantes} días` : 'Sin obligaciones próximas',
                  },
                  {
                    ok: true,
                    titulo: k.iva_saldo_favor_periodo > 0 ? 'IVA con saldo a favor' : 'IVA al corriente',
                    sub: k.iva_saldo_favor_periodo > 0 ? `${fmt(k.iva_saldo_favor_periodo)} acreditable` : `A pagar ${fmt(k.iva_por_pagar_periodo)}`,
                  },
                  {
                    ok: cfg.configurado,
                    titulo: 'Coeficiente de utilidad',
                    sub: cfg.configurado ? `CU ${cfg.coeficiente_utilidad}` : 'Sin configurar — ISR en $0',
                  },
                  {
                    ok: (cfg.clientes_con_tasa || 0) > 0,
                    warn: (cfg.clientes_con_tasa || 0) === 0,
                    titulo: 'Tasas IVA por cliente',
                    sub: (cfg.clientes_con_tasa || 0) > 0 ? `${cfg.clientes_con_tasa} cliente(s) configurados` : 'Usa % global — configura tus top clientes',
                  },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3.5 py-3 shadow-sm">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ background: s.ok ? '#10B981' : (s.warn ? '#D97706' : '#EF4444') }} />
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate">{s.titulo}</p>
                      <p className="text-[11px] text-slate-500 truncate">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══ TAB: RETENCIONES ══ */}
          {tab === 'retenciones' && (
            <div className="space-y-4">
              {loadingRet && (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando facturas con retenciones…
                </div>
              )}
              {!loadingRet && retenciones && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Kpi label="ISR retenido por clientes" value={fmt(retenciones.totales?.emitidas_ret_isr)}
                         sub="A tu favor — acreditable" tag="a favor" tagColor="bg-emerald-50 text-emerald-600" />
                    <Kpi label="IVA retenido por clientes" value={fmt(retenciones.totales?.emitidas_ret_iva)}
                         sub="Lo entera el cliente al SAT" tag="a favor" tagColor="bg-emerald-50 text-emerald-600" />
                    <Kpi label="ISR retenido a proveedores" value={fmt(retenciones.totales?.recibidas_ret_isr)}
                         sub="A enterar al SAT (día 17)" tag="a enterar" tagColor="bg-red-50 text-red-500" />
                    <Kpi label="IVA retenido a proveedores" value={fmt(retenciones.totales?.recibidas_ret_iva)}
                         sub="A enterar al SAT (día 17)" tag="a enterar" tagColor="bg-red-50 text-red-500" />
                  </div>
                  {[
                    { key: 'emitidas', titulo: '📥 Facturas emitidas donde el cliente te retuvo',
                      nota: 'Retenciones a tu favor: se acreditan contra tu ISR provisional (y el IVA retenido lo entera el cliente).',
                      col: 'Cliente' },
                    { key: 'recibidas', titulo: '📤 Facturas recibidas donde retuviste al proveedor',
                      nota: 'Retenciones a cargo: se enteran al SAT a más tardar el día 17 del mes siguiente.',
                      col: 'Proveedor' },
                  ].map(sec => (
                    <div key={sec.key} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                      <h3 className="font-bold text-slate-800 text-sm">{sec.titulo}</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5 mb-3">{sec.nota}</p>
                      {(retenciones[sec.key] || []).length === 0 ? (
                        <p className="text-xs text-slate-400 py-4 text-center">
                          Sin facturas con retenciones en {retenciones.ejercicio}.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-slate-400 border-b border-slate-100">
                                <th className="py-2 pr-3 font-semibold">Fecha</th>
                                <th className="py-2 pr-3 font-semibold">{sec.col}</th>
                                <th className="py-2 pr-3 font-semibold">RFC</th>
                                <th className="py-2 pr-3 font-semibold text-right">Subtotal</th>
                                <th className="py-2 pr-3 font-semibold text-right">Ret. ISR</th>
                                <th className="py-2 pr-3 font-semibold text-right">Ret. IVA</th>
                                <th className="py-2 font-semibold text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(retenciones[sec.key] || []).map((f, i) => (
                                <tr key={f.uuid || i} className="border-b border-slate-50 hover:bg-slate-50">
                                  <td className="py-2 pr-3 font-mono text-slate-500">{f.fecha}</td>
                                  <td className="py-2 pr-3 text-slate-700">{f.contraparte}</td>
                                  <td className="py-2 pr-3 font-mono text-slate-400">{f.rfc}</td>
                                  <td className="py-2 pr-3 font-mono text-right">{fmt(f.subtotal)}</td>
                                  <td className="py-2 pr-3 font-mono text-right text-indigo-600">{fmt(f.ret_isr)}</td>
                                  <td className="py-2 pr-3 font-mono text-right text-indigo-600">{fmt(f.ret_iva)}</td>
                                  <td className="py-2 font-mono text-right font-semibold">{fmt(f.total)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ══ TAB: ESTRATEGIAS IA ══ */}
          {tab === 'estrategias' && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-extrabold text-slate-800">✨ Estrategias sugeridas por el CFO Virtual</p>
                  <p className="text-xs text-slate-400 mt-0.5">Generadas con IA a partir de tus números reales del ejercicio. Valídalas con tu contador.</p>
                </div>
                <button
                  onClick={generarEstrategias}
                  disabled={loadingEstr}
                  className="px-4 py-2 rounded-lg text-white text-[13px] font-bold disabled:opacity-60 shrink-0"
                  style={{ background: BRAND }}
                  data-testid="btn-generar-estrategias"
                >
                  {loadingEstr ? 'Analizando…' : (estrategias ? 'Regenerar' : 'Generar estrategias')}
                </button>
              </div>

              {loadingEstr && (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> El CFO Virtual está analizando tu situación fiscal…
                </div>
              )}

              {!loadingEstr && !estrategias && (
                <p className="text-sm text-slate-400 py-10 text-center">
                  Presiona "Generar estrategias" para obtener 3-5 recomendaciones fiscales basadas en tu ISR, IVA, deducciones y calendario.
                </p>
              )}

              {!loadingEstr && estrategias && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(estrategias.estrategias || []).map((e, i) => (
                      <div key={i} className="border border-slate-200 rounded-xl p-4"
                           style={{ borderLeft: `4px solid ${BRAND}` }} data-testid={`estrategia-${i}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <p className="text-[13px] font-bold text-slate-800">{e.titulo}</p>
                          {e.prioridad && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${PRIO_STYLE[e.prioridad] || PRIO_STYLE.baja}`}>
                              {e.prioridad}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed mb-2">{e.detalle}</p>
                        {e.impacto && (
                          <p className="text-xs font-mono font-bold text-emerald-600">→ {e.impacto}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {estrategias.fuente === 'reglas' && (
                    <p className="text-[11px] text-slate-400 mt-3">
                      Generadas con reglas locales (IA no disponible en este momento).
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ TAB: CALENDARIO SAT ══ */}
          {tab === 'calendario' && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-[13px] font-bold text-slate-700 flex items-center gap-2">
                  <CalendarClock size={14} style={{ color: BRAND }} /> Próximas obligaciones SAT
                </h3>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: BRAND_SOFT, color: BRAND_DARK, border: `1px solid ${BRAND_BORDER}` }}>
                  montos estimados con tus CFDIs
                </span>
              </div>
              <div className="p-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400">
                      <th className="text-left py-2 px-3 font-semibold">Obligación</th>
                      <th className="text-left py-2 px-3 font-semibold">Periodo</th>
                      <th className="text-right py-2 px-3 font-semibold">Monto estimado</th>
                      <th className="text-left py-2 px-3 font-semibold">Vence</th>
                      <th className="text-left py-2 px-3 font-semibold">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.calendario || []).map((o, i) => (
                      <tr key={i} className="border-b border-slate-50" data-testid={`obligacion-${i}`}>
                        <td className="py-2.5 px-3 font-medium text-slate-700">{o.obligacion}</td>
                        <td className="py-2.5 px-3 text-slate-500">{o.periodo}</td>
                        <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-800">
                          {o.monto_estimado != null ? fmt(o.monto_estimado) : '—'}
                          {o.detalle && (
                            <div className="text-[10px] font-normal text-slate-400">
                              ISR {fmt(o.detalle.isr)} · IVA {fmt(o.detalle.iva)}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600">{fechaCorta(o.vence)}</td>
                        <td className="py-2.5 px-3"><PillDias dias={o.dias_restantes} proyectado={o.proyectado} /></td>
                      </tr>
                    ))}
                    {(data.calendario || []).length === 0 && (
                      <tr><td colSpan="5" className="py-8 text-center text-slate-400">Sin obligaciones próximas calculables.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="px-4 pb-4 text-[11px] text-slate-400">
                Fechas al día 17 recorridas si caen en fin de semana. No considera días adicionales por sexto dígito del RFC ni días inhábiles oficiales — confirma con tu contador.
              </p>
            </div>
          )}

          {/* ══ TAB: IVA & DEDUCCIONES ══ */}
          {tab === 'iva' && (
            <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-[13px] font-bold text-slate-700">⚖️ IVA del periodo ({k.periodo || '—'})</h3>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: BRAND_SOFT, color: BRAND_DARK, border: `1px solid ${BRAND_BORDER}` }}>
                    {data.iva_base === 'flujo' ? 'base: cobrado/pagado real (LIVA 1-B)' : 'base: CFDIs (devengado aprox.)'}
                  </span>
                </div>
                <div className="p-4 space-y-4">
                  {data.iva_base === 'flujo' && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-slate-50 rounded-lg p-2.5">
                        <p className="text-[11px] text-slate-500">Cobrado en {k.periodo}</p>
                        <p className="font-mono font-bold text-sm">{fmt(k.cobrado_periodo)}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2.5">
                        <p className="text-[11px] text-slate-500">Pagado con IVA en {k.periodo}</p>
                        <p className="font-mono font-bold text-sm">{fmt(k.pagado_periodo)}</p>
                      </div>
                    </div>
                  )}
                  {data.iva_base === 'flujo' && (
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      {cfg.clientes_con_tasa > 0
                        ? <>✓ {cfg.clientes_con_tasa} cliente(s) con tasa de IVA propia — {fmt(k.cobros_con_tasa_periodo)} de los cobros del mes usan su tasa; el resto aplica el {cfg.pct_actos_gravados}% gravado global.</>
                        : <>Ningún cliente tiene tasa de IVA configurada: todos los cobros usan el {cfg.pct_actos_gravados}% global. Puedes asignarla por cliente en <b>Catálogo → Clientes</b>.</>}
                      {' '}
                      {cfg.proveedores_con_tasa > 0
                        ? <>✓ {cfg.proveedores_con_tasa} proveedor(es) con tasa propia en el acreditable.</>
                        : <>Para el acreditable también puedes asignar tasa por proveedor en <b>Catálogo → Proveedores</b>.</>}
                    </p>
                  )}
                  {[
                    { lbl: data.iva_base === 'flujo' ? 'IVA trasladado (16/116 de lo cobrado)' : 'IVA trasladado (ventas)',
                      val: k.iva_trasladado_periodo, color: BRAND, pct: 100 },
                    { lbl: data.iva_base === 'flujo' ? 'IVA acreditable (16/116 de lo pagado con IVA)' : 'IVA acreditable (gastos con CFDI)',
                      val: k.iva_acreditable_periodo, color: '#10B981',
                      pct: k.iva_trasladado_periodo > 0 ? Math.min((k.iva_acreditable_periodo / k.iva_trasladado_periodo) * 100, 100) : 0 },
                  ].map(row => (
                    <div key={row.lbl}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-semibold text-slate-600">{row.lbl}</span>
                        <span className="font-mono text-sm font-bold">{fmt(row.val)}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${row.pct}%`, background: row.color }} />
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center rounded-lg px-4 py-3" style={{ background: '#F7F8FA' }}>
                    <b className="text-xs">IVA por pagar {k.periodo}</b>
                    <b className={`font-mono ${k.iva_por_pagar_periodo < 0 ? 'text-emerald-600' : ''}`}>
                      {k.iva_por_pagar_periodo < 0 ? `${fmt(Math.abs(k.iva_por_pagar_periodo))} a favor` : fmt(k.iva_por_pagar_periodo)}
                    </b>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-[13px] font-bold text-slate-700">🧾 Gasto por categoría (ejercicio {ejercicio})</h3>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: BRAND_SOFT, color: BRAND_DARK, border: `1px solid ${BRAND_BORDER}` }}>
                    fuente: categorización de CFDIs
                  </span>
                </div>
                <div className="p-2 max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-slate-100 text-slate-400">
                        <th className="text-left py-2 px-3 font-semibold">Categoría</th>
                        <th className="text-right py-2 px-3 font-semibold">Gasto acumulado</th>
                        <th className="text-center py-2 px-3 font-semibold">CFDIs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.deducciones || []).map((d, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className={`py-2 px-3 font-medium ${d.categoria === 'Sin categorizar' ? 'text-amber-600' : 'text-slate-700'}`}>
                            {d.categoria}
                            {d.categoria === 'Sin categorizar' && <span className="text-[9px] ml-1">⚠️ revisar deducibilidad</span>}
                          </td>
                          <td className="py-2 px-3 text-right font-mono">{fmt(d.total)}</td>
                          <td className="py-2 px-3 text-center text-slate-400 font-mono">{d.docs}</td>
                        </tr>
                      ))}
                      {(data.deducciones || []).length === 0 && (
                        <tr><td colSpan="3" className="py-8 text-center text-slate-400">Sin CFDIs de gasto en el ejercicio.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Cédula mensual de IVA: cobrado → trasladado, pagado → acreditable, arrastre de saldo a favor */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-[13px] font-bold text-slate-700">📅 Cédula mensual de IVA {ejercicio}</h3>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: BRAND_SOFT, color: BRAND_DARK, border: `1px solid ${BRAND_BORDER}` }}>
                  saldo a favor se acredita al mes siguiente (LIVA 6)
                </span>
              </div>
              <div className="p-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400">
                      <th className="text-left py-2 px-2 font-semibold">Mes</th>
                      <th className="text-right py-2 px-2 font-semibold">Cobrado</th>
                      <th className="text-right py-2 px-2 font-semibold">IVA trasladado</th>
                      <th className="text-right py-2 px-2 font-semibold">Pagado c/IVA</th>
                      <th className="text-right py-2 px-2 font-semibold">IVA acreditable</th>
                      <th className="text-right py-2 px-2 font-semibold">Causado del mes</th>
                      <th className="text-right py-2 px-2 font-semibold">A pagar</th>
                      <th className="text-right py-2 px-2 font-semibold">Saldo a favor</th>
                      <th className="text-right py-2 px-2 font-semibold">Ret. a enterar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meses.map(m => (
                      <tr key={m.mes} className={`border-b border-slate-50 ${!m.es_real ? 'opacity-60' : ''}`} data-testid={`iva-mes-${m.mes}`}>
                        <td className="py-1.5 px-2 font-medium text-slate-700">
                          {m.label} {!m.es_real && <span className="text-[9px] text-slate-400">(proy.)</span>}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-600">{m.cobrado != null ? fmt(m.cobrado) : '—'}</td>
                        <td className="py-1.5 px-2 text-right font-mono" style={{ color: BRAND_DARK }}>{fmt(m.iva_trasladado)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-600">{m.pagado != null ? fmt(m.pagado) : '—'}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-emerald-600">{fmt(m.iva_acreditable)}</td>
                        <td className={`py-1.5 px-2 text-right font-mono ${m.iva_causado < 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                          {m.iva_causado < 0 ? `(${fmt(Math.abs(m.iva_causado))})` : fmt(m.iva_causado)}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono font-semibold text-slate-900">
                          {m.iva_por_pagar > 0 ? fmt(m.iva_por_pagar) : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono text-emerald-600">
                          {m.iva_saldo_favor > 0 ? fmt(m.iva_saldo_favor) : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono text-amber-600"
                            title={m.retenciones > 0 ? `ISR retenido ${fmt(m.ret_isr)} · IVA retenido ${fmt(m.ret_iva)}` : ''}>
                          {m.retenciones > 0 ? fmt(m.retenciones) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-4 pb-3 text-[11px] text-slate-400">
                Causado del mes = IVA trasladado − IVA acreditable. Si es negativo genera saldo a favor,
                que se acredita automáticamente contra el IVA a cargo de los meses siguientes.
                "A pagar" es el importe neto después de ese acreditamiento.
                "Ret. a enterar" = ISR + IVA retenidos a proveedores con régimen de retención
                (honorarios, arrendamiento, fletes, RESICO — configurable en <b>Catálogo → Proveedores</b>);
                se enteran al SAT el día 17 junto con el pago provisional y aparecen en el Calendario SAT.
              </p>
            </div>
            </>
          )}

          <p className="text-[11px] text-slate-400 pb-2">{data.nota}</p>
        </>
      )}

      {showConfig && (
        <ConfigModal
          config={cfg}
          onClose={() => setShowConfig(false)}
          onSaved={() => { setShowConfig(false); cargar(); }}
        />
      )}
    </div>
  );
}
